/**
 * Person Card tabbed modal — replaces the legacy 4-tab EditMemberDialog
 * (and the interim accordion layout, which hid sections from users who
 * didn't notice the collapsed headers).
 *
 * Tabs:
 *   1. Plan (Tier & Add-ons)  — default; role-gated to Clinician
 *   2. Roles & Permissions    — role checkboxes + prescribing-proxy sub-block
 *   3. Supervision            — co-sign toggle + supervisory claim submission
 *   4. Basic Info             — account name/contact; autosaves via useAutoSave
 *   5. Billing & Claims       — claims/document identity; autosaves via useAutoSave
 *
 * Footer:
 *   - Cancel
 *   - "Confirm — $X/mo" when plan changes pending
 *   - "Save" when only role/membership changes pending
 *   - disabled when nothing's pending (profile/billing autosave handles theirs)
 */

import { useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { formatPrice } from '@features/subscriptions';
import { ProviderBadge, ScrollFade } from '@jotpsych/ui';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import { ROLES } from '@utils/roleUtils';

import type { OrganizationMemberTableRow } from '../../types';

import { BillingSection } from './BillingSection';
import { ProfileSection } from './ProfileSection';
import { RolesSection } from './RolesSection';
import { SupervisionSection } from './SupervisionSection';
import { TierAddOnsSection } from './TierAddOnsSection';
import { usePersonCard } from './usePersonCard';

type TabKey = 'tier' | 'roles' | 'supervision' | 'profile' | 'billing';

// Default fallback role list when the parent doesn't pass availableRoles in
// (mirrors the legacy EditMemberDialog defaults).
const DEFAULT_ROLES: ProviderRoleInfo[] = [
  { role: ROLES.CLINICIAN, display_name: 'Clinician', description: 'Clinical staff member' },
  {
    role: ROLES.CLINICAL_STAFF,
    display_name: 'Clinical Staff',
    description: 'Records encounters and enters clinical information; cannot generate notes',
  },
  {
    role: ROLES.OPERATIONS_SUPPORT,
    display_name: 'Operations Support',
    description: 'Operations team member',
  },
  {
    role: ROLES.PRACTICE_ADMINISTRATOR,
    display_name: 'Practice Administrator',
    description: 'Practice admin with billing access',
  },
  {
    role: ROLES.BILLING_ADMINISTRATOR,
    display_name: 'Billing Administrator',
    description: 'Billing-only access',
  },
  { role: ROLES.SUPERVISOR, display_name: 'Supervisor', description: 'Clinical supervisor' },
];

interface PersonCardModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  member: OrganizationMemberTableRow | null;
  availableRoles?: ProviderRoleInfo[];
}

export function PersonCardModal({
  open,
  onClose,
  onSuccess,
  member,
  availableRoles = [],
}: PersonCardModalProps) {
  const { practice } = useEventTracking();
  const card = usePersonCard({ member, open, onSuccess });

  const [tab, setTab] = useState<TabKey>('tier');
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);

  // Reset to the default tab + fire opened event when the modal opens.
  useEffect(() => {
    if (open && member) {
      setTab('tier');
      practice.personCardOpened({
        member_id: member.userId,
        role_count: member.roles.length,
        has_tier: !!member.bundles?.find(b => b.bundle_type === 'tier' && b.status === 'active'),
        addon_count:
          member.bundles?.filter(b => b.bundle_type === 'add_on' && b.status === 'active').length ||
          0,
      });
    }
  }, [open, member, practice]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, next: TabKey) => {
      setTab(next);
      // Same engagement event the accordion layout emitted on expand.
      if (member) {
        practice.personCardSectionExpanded({
          member_id: member.userId,
          section: next,
        });
      }
    },
    [member, practice],
  );

  const handleClose = useCallback(() => {
    if (card.isSaving) return;
    onClose();
  }, [card.isSaving, onClose]);

  const handleCommit = useCallback(() => {
    card.commitChanges();
  }, [card]);

  if (!member) return null;

  const rolesToDisplay = availableRoles.length > 0 ? availableRoles : DEFAULT_ROLES;

  // Footer label / disabled logic
  const hasFooterChanges = card.hasPlanChanges || card.hasRolesChanges || card.hasMembershipChanges;
  const projectedPriceStr = formatPrice(card.projectedCost.cents, card.projectedCost.currency);
  const footerLabel = card.hasPlanChanges
    ? card.isTrialing
      ? `Confirm — ${projectedPriceStr}/mo after trial`
      : `Confirm — ${projectedPriceStr}/mo`
    : 'Save';
  const footerDisabled =
    card.isSaving ||
    !hasFooterChanges ||
    (card.canSelectBundles && card.holdsPaidSeat && !card.selectedTierId);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <ProviderBadge name={member.name} size="regular" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {member.name || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {member.email || 'No email'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={card.isSaving}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mt: 1.5, minHeight: 40 }}
        >
          <Tab label="Plan" value="tier" sx={{ minHeight: 40 }} />
          <Tab label="Roles & Permissions" value="roles" sx={{ minHeight: 40 }} />
          <Tab label="Supervision" value="supervision" sx={{ minHeight: 40 }} />
          <Tab label="Basic Info" value="profile" sx={{ minHeight: 40 }} />
          <Tab label="Billing & Claims" value="billing" sx={{ minHeight: 40 }} />
        </Tabs>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}
      >
        <ScrollFade>
          {card.isLoading ? (
            <Box sx={{ p: 3 }}>
              <Skeleton variant="rounded" sx={{ height: 320, mb: 2 }} />
              <Skeleton variant="rounded" sx={{ height: 80 }} />
            </Box>
          ) : (
            <Box sx={{ px: 3, py: 3 }}>
              {tab === 'tier' && (
                <TierAddOnsSection
                  holdsPaidSeat={card.holdsPaidSeat}
                  seatClassChanged={card.seatClassChanged}
                  canSelectBundles={card.canSelectBundles}
                  isCatalogLoading={card.isCatalogLoading}
                  tiers={card.eligibleTiers}
                  availableAddOns={card.availableAddOns}
                  selectedTierId={card.selectedTierId}
                  selectedAddOnIds={card.selectedAddOnIds}
                  currentTierBundleId={card.currentTierBundleId}
                  onTierChange={card.changeTier}
                  onToggleAddOn={card.toggleAddOn}
                  isTrialing={card.isTrialing}
                  hasPlanChanges={card.hasPlanChanges}
                  currentCost={card.currentCost}
                  projectedCost={card.projectedCost}
                  billingInterval="month"
                />
              )}

              {tab === 'roles' && (
                <RolesSection
                  rolesToDisplay={rolesToDisplay}
                  selectedRoles={card.selectedRoles}
                  onToggleRole={card.toggleRole}
                  canPrepareOnBehalf={card.canPrepareOnBehalf}
                  onCanPrepareOnBehalfChange={card.setCanPrepareOnBehalf}
                  disabled={card.isSaving}
                />
              )}

              {tab === 'supervision' && (
                <SupervisionSection
                  userId={member.userId}
                  billingInfo={card.memberDetail?.billing_info}
                  isClinician={card.isClinician}
                  isClinicalStaff={card.isClinicalStaff}
                  needsCosign={card.needsCosign}
                  onNeedsCosignChange={card.setNeedsCosign}
                  supervisorUserId={card.supervisorUserId}
                  onSupervisorChange={card.setSupervisorUserId}
                  disabled={card.isSaving}
                />
              )}

              {tab === 'profile' && (
                <ProfileSection
                  initialData={card.profileData}
                  onSave={card.persistProfile}
                  disabled={card.isSaving}
                />
              )}

              {tab === 'billing' && (
                <>
                  {!card.billingData.npi_number && (
                    <Alert
                      severity="info"
                      sx={{ mb: 3 }}
                      action={
                        <Button color="inherit" size="small" onClick={() => setTab('supervision')}>
                          Go to Supervision
                        </Button>
                      }
                    >
                      This clinician has no NPI on file. If they bill under a supervising provider
                      (e.g. interns), set that up in the Supervision tab instead.
                    </Alert>
                  )}
                  <BillingSection
                    initialData={card.billingData}
                    onSave={card.persistBilling}
                    disabled={card.isSaving}
                    billingEntities={card.billingEntities}
                    entitiesLoading={card.entitiesLoading}
                    serviceFacilities={card.serviceFacilities}
                    facilitiesLoading={card.facilitiesLoading}
                  />
                </>
              )}
            </Box>
          )}
        </ScrollFade>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        {!card.isEditingSelf ? (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmDeactivateOpen(true)}
            disabled={card.isSaving || card.isDeactivating}
          >
            Deactivate Member
          </Button>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={card.isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCommit}
            color="primary"
            variant="contained"
            disabled={footerDisabled}
            startIcon={card.isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {card.isSaving ? 'Saving…' : footerLabel}
          </Button>
        </Box>
      </DialogActions>

      <ConfirmDialog
        open={confirmDeactivateOpen}
        variant="error"
        title="Deactivate Member?"
        message={`Are you sure you want to deactivate ${
          member.name || 'this member'
        }? They will lose access to the organization and their subscription will be cancelled.`}
        onClose={() => setConfirmDeactivateOpen(false)}
        onConfirm={card.deactivate}
        cancelText="Cancel"
        confirmText="Deactivate"
        loading={card.isDeactivating}
      />
    </Dialog>
  );
}
